import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

interface AvatarProps {
  /** Full name, used to derive initials. */
  name?: string | null;
  /** Fallback source for initials when there is no name. */
  email?: string | null;
  /** Optional image; falls back to initials when absent or it fails to load. */
  src?: string | null;
  className?: string;
}

/**
 * Derive up to two initials: first letters of the first and last word, so
 * "Ada Lovelace" reads "AL" and a single-word name reads its first letter.
 */
export function getInitials(name?: string | null, email?: string | null) {
  const source = (name ?? "").trim();
  if (source) {
    const words = source.split(/\s+/).filter(Boolean);
    const first = words[0]?.[0] ?? "";
    const last = words.length > 1 ? words[words.length - 1][0] : "";
    return (first + last).toUpperCase();
  }
  const fallback = (email ?? "").trim();
  return fallback ? fallback[0].toUpperCase() : "?";
}

/**
 * Small circular identity marker for rows that represent a person or entity.
 * Initials on primary-100 with primary-700 text when no image is available.
 */
export default function Avatar({ name, email, src, className }: AvatarProps) {
  const [failed, setFailed] = React.useState(false);
  const initials = getInitials(name, email);

  const base = cn(
    "inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 overflow-hidden",
    className
  );

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        onError={() => setFailed(true)}
        className={cn(base, "object-cover")}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        base,
        "bg-primary-100 text-primary-700 text-xs font-semibold"
      )}
    >
      {initials}
    </span>
  );
}
